import { useEffect, useState, type ChangeEvent } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import {
  Appearance,
  loadStripe,
  StripeElementsOptions,
} from "@stripe/stripe-js";
import axios from "axios";
import { slackHook } from "src/utils/snug/slackHooks";
import "./starter-pay.css";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
);

type PlanKey = "annual";

interface PlanConfig {
  label: string;
  amount: number;
  display: string;
  todayLabel: string;
  thereafter: string;
  bestFor: string;
  commitment: string;
  onboarding: string;
  onboardingQualifier: string;
  platform: string;
  platformQualifier: string;
  trust: string;
  will: string;
  promo: string;
  promoTerms: string;
  promoBadge: string;
}

const PLANS: Record<PlanKey, PlanConfig> = {
  annual: {
    label: "Starter",
    amount: 3000,
    display: "$3,000",
    todayLabel: "Annual fee",
    thereafter: "",
    bestFor:
      "Teams ready to commit and looking to maximize value from day one.",
    commitment: "Annual",
    onboarding: "Waived",
    onboardingQualifier: "",
    platform: "$3,000/yr",
    platformQualifier: "due today",
    trust: "$300",
    will: "$75",
    promo: "",
    promoTerms: "",
    promoBadge: "",
  },
};

// Fallback values used when the custom plan builder doesn't supply a given
// field. Kept separate from PLANS.annual so the public default plan can
// evolve independently of custom-plan fallbacks.
const CUSTOM_PLAN_BASE: PlanConfig = {
  label: "Custom Plan",
  amount: 3000,
  display: "$3,000",
  todayLabel: "Onboarding fee",
  thereafter: "",
  bestFor: "Teams that want to get started before committing to a full year.",
  commitment: "Monthly",
  onboarding: "$3,000",
  onboardingQualifier: "first 3 months",
  platform: "$500/mo",
  platformQualifier: "starts month 4",
  trust: "$500",
  will: "$150",
  promo: "",
  promoTerms: "",
  promoBadge: "",
};

// Custom plan via URL params. When ANY of these are present, the page enters
// custom mode: the Monthly/Annual toggle is hidden and the table renders a
// single column populated from the params. Missing params fall back to the
// Annual configuration. `billed` controls the actual amount charged.
const CUSTOM_PARAMS = [
  "label",
  "billed",
  "today_label",
  "thereafter",
  "show_platform_qualifier",
  "onboarding",
  "platform",
  "trust",
  "will",
  "commitment",
  "promo",
  "promo_terms",
  "promo_badge",
] as const;

function formatFee(raw: string | null, fallback: string): string {
  if (raw == null) return fallback;
  const m = raw.match(/^\s*\$?\s*([\d,]+)(.*)$/);
  if (!m) return raw;
  const num = parseInt(m[1].replace(/,/g, ""), 10);
  if (!Number.isFinite(num)) return raw;
  return "$" + num.toLocaleString("en-US") + (m[2] || "");
}

function parseCustomPlan(): PlanConfig | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const hasAny = CUSTOM_PARAMS.some((p) => params.has(p));
  if (!hasAny) return null;

  const base = CUSTOM_PLAN_BASE;
  const billedDigits = (params.get("billed") || "").replace(/[^0-9]/g, "");
  const billed = billedDigits ? parseInt(billedDigits, 10) : 0;
  const hasBilled = billed > 0;

  return {
    label: params.get("label") || "Custom Plan",
    amount: hasBilled ? billed : base.amount,
    display: hasBilled ? "$" + billed.toLocaleString("en-US") : base.display,
    todayLabel: params.get("today_label") || base.todayLabel,
    thereafter: params.get("thereafter") || "",
    bestFor: base.bestFor,
    commitment: params.get("commitment") || base.commitment,
    onboarding: formatFee(params.get("onboarding"), base.onboarding),
    onboardingQualifier: base.onboardingQualifier,
    platform: formatFee(params.get("platform"), base.platform),
    platformQualifier: params.get("show_platform_qualifier") === "1" ? base.platformQualifier : "",
    trust: formatFee(params.get("trust"), base.trust),
    will: formatFee(params.get("will"), base.will),
    promo: params.get("promo") || "",
    promoTerms: params.get("promo_terms") || "",
    promoBadge: params.get("promo_badge") || "",
  };
}

const CUSTOM_PLAN: PlanConfig | null = parseCustomPlan();

const stripeAppearance: Appearance = {
  theme: "stripe",
  variables: {
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontLineHeight: "1.5",
    borderRadius: "12px",
    colorBackground: "#F2F2EF",
    colorPrimary: "#3045C9",
    colorPrimaryText: "#0D0D12",
    colorText: "#0D0D12",
    colorTextSecondary: "#5A5A68",
    colorTextPlaceholder: "#8E8E9A",
    colorDanger: "#C93050",
    spacingUnit: "4px",
  },
  rules: {
    ".Block": {
      backgroundColor: "var(--colorBackground)",
      boxShadow: "none",
      padding: "12px",
    },
    ".Input": {
      padding: "12px 14px",
      backgroundColor: "#F2F2EF",
      border: "1px solid transparent",
      boxShadow: "none",
    },
    ".Input:focus": {
      backgroundColor: "#FFFFFF",
      border: "1px solid #3045C9",
      boxShadow: "0 0 0 3px rgba(48, 69, 201, 0.12)",
    },
    ".Tab": {
      padding: "10px 12px 8px 12px",
      border: "1px solid rgba(0,0,0,0.06)",
    },
    ".Tab--selected, .Tab--selected:focus, .Tab--selected:hover": {
      border: "1px solid #3045C9",
      backgroundColor: "#FFFFFF",
      boxShadow: "0 0 0 3px rgba(48, 69, 201, 0.12)",
    },
    ".Label": {
      fontWeight: "600",
      fontSize: "11px",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#8E8E9A",
    },
  },
};

interface FormData {
  name: string;
  company: string;
  email: string;
  plan: PlanKey;
  terms: boolean;
}

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 8.5l3 3 7-7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="7"
        width="10"
        height="7"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 7V5a2.5 2.5 0 015 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PaymentForm({
  formData,
  plan,
  onSuccess,
}: {
  formData: FormData;
  plan: PlanConfig;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isCustom = CUSTOM_PLAN !== null;

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");

    const { paymentIntent, error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message || "Payment failed. Please try again.");
      setLoading(false);
      return;
    }

    if (
      paymentIntent?.status === "succeeded" ||
      paymentIntent?.status === "processing"
    ) {
      slackHook(
        `🎉 STARTER PAYMENT ${paymentIntent.status.toUpperCase()}\n` +
          `Name: ${formData.name}\n` +
          `Company: ${formData.company}\n` +
          `Email: ${formData.email}\n` +
          `Plan: ${plan.label} (${plan.display})`,
      );
      onSuccess();
    } else {
      setError("Payment could not be completed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <div className="sp-card">
        <div className="sp-card-head">
          <h2 className="sp-section-title">Payment details</h2>
          <p className="sp-section-help">
            Securely processed by Stripe. Your card is charged once for the
            amount shown below.
          </p>
        </div>
        <div className="sp-stripe">
          <PaymentElement />
        </div>
        {error && <div className="sp-error">{error}</div>}
      </div>

      <div className="sp-statement" aria-live="polite">
        <div className="sp-statement-head">
          <span className="sp-statement-kicker">Order summary</span>
          <span className="sp-statement-tier">
            {plan.label}{" "}
            <span className="sp-statement-tier-dot" aria-hidden="true" /> Snug
          </span>
        </div>

        {!isCustom && (
          <div className="sp-statement-section">
            <div className="sp-statement-row">
              <span>{plan.todayLabel}</span>
              <span className="sp-num">{plan.display}</span>
            </div>
            <div className="sp-statement-row">
              <span>{plan.thereafter}</span>
            </div>
          </div>
        )}

        {isCustom && plan.thereafter && (
          <div className="sp-statement-section">
            <div className="sp-statement-row">
              <span>{plan.thereafter}</span>
            </div>
          </div>
        )}

        <div className="sp-statement-section sp-statement-totals">
          <div className="sp-statement-row sp-statement-headline-row">
            <span>Billed today</span>
            <span className="sp-headline-num">{plan.display}</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="sp-cta"
        disabled={loading || !stripe}
        onClick={handleSubmit}
      >
        {loading ? (
          <span className="sp-cta-spinner" aria-hidden="true" />
        ) : (
          <LockIcon />
        )}
        Pay {plan.display} and get started
      </button>
    </>
  );
}

export default function StarterPayPage() {
  const [step, setStep] = useState<"form" | "payment" | "success">("form");
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    email: "",
    plan: "annual",
    terms: false,
  });

  const apiBase = process.env.REACT_APP_SNUG_API_URL || "";

  useEffect(() => {
    if (!CUSTOM_PLAN) return;
    const params = new URLSearchParams(window.location.search);
    let mutated = false;
    for (const p of CUSTOM_PARAMS) {
      if (params.has(p)) {
        params.delete(p);
        mutated = true;
      }
    }
    if (!mutated) return;
    const search = params.toString();
    const newUrl =
      window.location.pathname +
      (search ? `?${search}` : "") +
      window.location.hash;
    window.history.replaceState(window.history.state, "", newUrl);
  }, []);

  if (!isGetSnugDomain) return null;

  const isCustom = CUSTOM_PLAN !== null;
  const plan: PlanConfig = isCustom
    ? (CUSTOM_PLAN as PlanConfig)
    : PLANS[formData.plan];

  const tiers: {
    key: string;
    label: string;
    config: PlanConfig;
    isActive: boolean;
  }[] = isCustom
    ? [{ key: "custom", label: plan.label, config: plan, isActive: true }]
    : [{ key: "annual", label: "Starter", config: PLANS.annual, isActive: true }];

  const activeTier = tiers.find((t) => t.isActive) ?? tiers[0];

  const planRows: { label: string; qualifier?: string; values: string[] }[] = [
    ...(isCustom
      ? [
          {
            label: "Onboarding fee",
            qualifier: activeTier.config.onboardingQualifier || undefined,
            values: tiers.map((t) => t.config.onboarding),
          },
        ]
      : []),
    {
      label: "Platform fee",
      qualifier: activeTier.config.platformQualifier || undefined,
      values: tiers.map((t) => t.config.platform),
    },
    { label: "Trust fee", values: tiers.map((t) => t.config.trust) },
    { label: "Will fee", values: tiers.map((t) => t.config.will) },
    ...(isCustom
      ? [{ label: "Commitment", values: tiers.map((t) => t.config.commitment) }]
      : []),
    { label: "Billed today", values: tiers.map((t) => t.config.display) },
  ];

  const setField = <K extends keyof FormData>(key: K, value: FormData[K]) =>
    setFormData((f) => ({ ...f, [key]: value }));

  const handleContinue = async () => {
    setError("");

    if (
      !formData.name.trim() ||
      !formData.company.trim() ||
      !formData.email.trim()
    ) {
      setError("Please fill in all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!formData.terms) {
      setError("You must agree to the Terms and Conditions.");
      return;
    }

    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        plan: isCustom ? "custom" : formData.plan,
        name: formData.name,
        company: formData.company,
        email: formData.email,
      };
      if (isCustom) {
        payload.amount = plan.amount;
        payload.label = plan.label;
      }
      const { data } = await axios.post(
        `${apiBase}/v3/starter-pay/payment-intent/`,
        payload,
      );
      setClientSecret(data.client_secret);
      setStep("payment");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      setError(
        e?.response?.data?.error || "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (step === "success") {
    return (
      <div className="sp-page">
        <div className="sp-brand">
          <img
            src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
            alt="Snug"
            className="sp-brand-logo"
          />
        </div>
        <div className="sp-success">
          <div className="sp-success-card">
            <div className="sp-success-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 12.5l4.5 4.5L20 6.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 className="sp-success-title">Welcome to Snug.</h1>
            <p className="sp-success-body">
              Your payment was successful. We'll reach out shortly to{" "}
              <strong>{formData.email}</strong> with onboarding details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isPayment = step === "payment";

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <div className="sp-eyebrow">
          <span className="sp-eyebrow-dot" aria-hidden="true" />
          {isPayment ? "Step 2 of 2" : "Starter Plan"}
        </div>

        <h1 className="sp-headline">
          {isPayment ? (
            <>
              Almost <em>there.</em>
            </>
          ) : (
            <>
              Get started with <em>Snug.</em>
            </>
          )}
        </h1>

        <p className="sp-sub">
          {isPayment
            ? `${plan.label} - ${plan.display}. Review your order and complete your secure payment below.`
            : isCustom
              ? "Your custom plan is set up below. Review the details and enter your information to continue with your team's onboarding and white-label platform setup."
              : "Includes full platform access, white-label setup, and onboarding support."}
        </p>

        {!isPayment && (
          <>
            <div className="sp-card sp-plan-card">
              <div className="sp-card-head">
                <span className="sp-card-eyebrow">Plan details</span>
              </div>
              <table className="sp-plan-table">
                <thead>
                  <tr>
                    <th scope="col" className="sp-plan-th-label">
                      &nbsp;
                    </th>
                    {tiers.map((t) => (
                      <th
                        key={t.key}
                        scope="col"
                        className={`sp-plan-th${t.isActive ? " is-active" : ""}`}
                      >
                        {t.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {planRows.map((row) => (
                    <tr key={row.label}>
                      <th scope="row" className="sp-plan-row-label">
                        {row.label}
                        {row.qualifier && (
                          <span className="sp-plan-row-qualifier">
                            ({row.qualifier})
                          </span>
                        )}
                      </th>
                      {row.values.map((v, i) => (
                        <td
                          key={i}
                          className={`sp-plan-cell${tiers[i].isActive ? " is-active" : ""}`}
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {plan.promo && (
              <div className="sp-promo-banner" role="note">
                <div className="sp-promo-icon" aria-hidden="true">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2l2.4 7.2H22l-6 4.8 2.4 7.2L12 16.4l-6.4 4.8 2.4-7.2-6-4.8h7.6L12 2z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="sp-promo-content">
                  <span className="sp-promo-title">{plan.promo}</span>
                  {plan.promoTerms && (
                    <span className="sp-promo-terms">{plan.promoTerms}</span>
                  )}
                </div>
                {plan.promoBadge && (
                  <span className="sp-promo-badge">{plan.promoBadge}</span>
                )}
              </div>
            )}

            <div className="sp-card">
              <div className="sp-card-head">
                <h2 className="sp-section-title">Your details</h2>
                <p className="sp-section-help">
                  Tell us who you are so we can set up your account and send
                  onboarding materials.
                </p>
              </div>
              <div className="sp-input-grid sp-input-grid-1">
                <label className="sp-input">
                  <span className="sp-input-label">Full name</span>
                  <input
                    type="text"
                    className="sp-input-field"
                    value={formData.name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setField("name", e.target.value)
                    }
                    placeholder="Jane Doe"
                  />
                </label>
                <label className="sp-input">
                  <span className="sp-input-label">Company name</span>
                  <input
                    type="text"
                    className="sp-input-field"
                    value={formData.company}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setField("company", e.target.value)
                    }
                    placeholder="Acme Advisors"
                  />
                </label>
                <label className="sp-input">
                  <span className="sp-input-label">Work email</span>
                  <input
                    type="email"
                    className="sp-input-field"
                    value={formData.email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setField("email", e.target.value)
                    }
                    placeholder="jane@acme.com"
                  />
                </label>
              </div>

              <label className="sp-check" style={{ marginTop: 16 }}>
                <input
                  type="checkbox"
                  checked={formData.terms}
                  onChange={(e) => setField("terms", e.target.checked)}
                />
                <span className="sp-check-box" aria-hidden="true">
                  <CheckIcon />
                </span>
                <span className="sp-check-label">
                  I have read and agree to the{" "}
                  <a
                    href="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/Professional+Terms+of+Service.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms and Conditions
                  </a>
                  .
                </span>
              </label>
            </div>

            <div className="sp-statement" aria-live="polite">
              <div className="sp-statement-head">
                <span className="sp-statement-kicker">Order summary</span>
                <span className="sp-statement-tier">
                  {plan.label}{" "}
                  <span className="sp-statement-tier-dot" aria-hidden="true" />{" "}
                  Snug
                </span>
              </div>

              {!isCustom && (
                <div className="sp-statement-section">
                  <div className="sp-statement-row">
                    <span>{plan.todayLabel}</span>
                    <span className="sp-num">{plan.display}</span>
                  </div>
                  <div className="sp-statement-row">
                    <span>{plan.thereafter}</span>
                  </div>
                </div>
              )}

              {isCustom && plan.thereafter && (
                <div className="sp-statement-section">
                  <div className="sp-statement-row">
                    <span>{plan.thereafter}</span>
                  </div>
                </div>
              )}

              <div className="sp-statement-section sp-statement-totals">
                <div className="sp-statement-row sp-statement-headline-row">
                  <span>Billed today</span>
                  <span className="sp-headline-num">{plan.display}</span>
                </div>
              </div>
            </div>

            {error && <div className="sp-error">{error}</div>}

            <button
              type="button"
              className="sp-cta"
              disabled={loading}
              onClick={handleContinue}
            >
              {loading ? (
                <span className="sp-cta-spinner" aria-hidden="true" />
              ) : (
                <LockIcon />
              )}
              Continue to payment
            </button>

            <p className="sp-disclaimer">
              Your card is processed securely by Stripe. We never store card
              details on our servers.
            </p>
          </>
        )}

        {isPayment && clientSecret && (
          <Elements
            stripe={stripePromise}
            options={
              {
                clientSecret,
                appearance: stripeAppearance,
                loader: "auto",
              } as StripeElementsOptions
            }
          >
            <PaymentForm
              formData={formData}
              plan={plan}
              onSuccess={() => setStep("success")}
            />
          </Elements>
        )}
      </section>
    </div>
  );
}
